// The ticket drawer (design handoff §4.6, prototype `Tickets B - Desk board`).
//
// 680px, slides from the right over a scrim; the one place a shadow is allowed.
// Header → facts → description → agent triage note → linked development →
// conversation → composer.
//
// ── THE COMPOSER SAFETY RULE ────────────────────────────────────────────────
// The drawer ALWAYS opens in `Internal note` mode and RESETS TO INTERNAL every
// single time it opens — including after the user switched it to customer mode,
// closed it, and reopened it.
//
// That reset is STRUCTURAL, not an effect I have to remember to fire: the body
// is a separate component, it is only mounted while a ticket is open, and it is
// `key`ed on the ticket key. Closing unmounts it; opening mounts a fresh one
// whose `useState<ComposerMode>("internal")` initialiser runs again. There is
// no code path that can carry customer mode across an open, because the state
// holding it no longer exists between opens.
//
// Customer reply is reached only via a quiet text button, and customer mode
// changes the footer loudly (amber banner, amber border, CUSTOMER REPLY label,
// amber "Send to customer" button) — see `composerLook`, which is pure and
// unit-tested. The mode is also sent explicitly to the server, which rejects
// anything that is not exactly "internal" or "customer" rather than defaulting.

import { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { brainTokens } from "../design/brainTheme";
import {
  getTicketDesk,
  postDeskNote,
  postTicketDiscussed,
  type DeskTicketDetail,
  type DeskThreadMessage,
  type ThreadRole,
} from "../lib/api";
import { AgentPanel } from "./AgentPanel";
import { PriorityPill } from "./RegisterTable";
import { DESK_STATE_LABEL, DESK_STATE_TONE, formatAgo } from "./deskModel";

const c = brainTokens.color;
const MONO = brainTokens.font.mono;

const TONE_COLOR = {
  danger: c.dangerText,
  warning: c.warnDot,
  teal: c.teal,
  neutral: c.placeholder,
  success: c.successDot,
} as const;

export type ComposerMode = "internal" | "customer";

function MonoLabelMicro({ children, color = c.faint }: { children: React.ReactNode; color?: string }) {
  return (
    <Box
      component="span"
      sx={{
        fontFamily: MONO,
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        color,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

const ROLE_LOOK: Record<ThreadRole, { label: string; bg: string; fg: string; dot: string; rule: string }> = {
  customer: { label: "CUSTOMER", bg: c.warnBg, fg: c.warnText, dot: c.warnDot, rule: c.warnBorder },
  aeronet: { label: "AERONET", bg: c.tealTint, fg: c.tealInk, dot: c.teal, rule: c.tealTintBorder },
  internal: { label: "INTERNAL NOTE", bg: c.neutralPillBg, fg: c.muted, dot: c.placeholder, rule: c.border },
};

export interface ComposerLook {
  /** The micro-label above the textarea. */
  label: string;
  /** The primary button's text. */
  submitLabel: string;
  /** True only in customer mode: amber banner, amber border, amber button. */
  loud: boolean;
  /** Token names, resolved by the renderer — never a raw hex. */
  accent: string;
  border: string;
}

/**
 * Everything that must change when the composer is in customer mode, in one
 * pure place so the "is it loud enough?" rule is testable rather than
 * scattered through JSX.
 */
export function composerLook(mode: ComposerMode): ComposerLook {
  return mode === "customer"
    ? { label: "Customer reply", submitLabel: "Send to customer", loud: true, accent: c.warnText, border: c.warnBorder }
    : {
        label: "Internal note · not sent to the customer",
        submitLabel: "Save internal note",
        loud: false,
        accent: c.teal,
        border: c.border,
      };
}

/** The mode a freshly-opened drawer starts in. Always internal. Always. */
export const INITIAL_COMPOSER_MODE: ComposerMode = "internal";

const CANNED: Record<ComposerMode, { label: string; text: string }[]> = {
  internal: [
    { label: "Decision", text: "Decided at standup: " },
    { label: "Blocked by", text: "Blocked by " },
  ],
  customer: [
    { label: "Committing to the next release", text: "We have this scoped into the next release. I will confirm once it is in UAT." },
    { label: "Need one more detail", text: "Before we can move this forward we need one more detail from you: " },
  ],
};

function formatWhen(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Whole days between an ISO timestamp and now — the "Raised Nd ago" fact. */
export function daysSince(iso: string, nowMs = Date.now()): number {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((nowMs - t) / 86_400_000));
}

export interface TicketDrawerProps {
  ticketKey: string | null;
  date?: string;
  /** Names of the people in the room — stamped onto the standup note. */
  attendees?: string[];
  onClose: () => void;
}

/**
 * The mount gate. While no ticket is open the body does not exist, so neither
 * does its composer state — which is what makes the internal-mode reset
 * structural rather than something an effect has to remember to do. The `key`
 * additionally forces a fresh mount when the room jumps straight from one
 * ticket to another without closing.
 */
export function TicketDrawer({ ticketKey, date, attendees, onClose }: TicketDrawerProps) {
  if (!ticketKey) return null;
  return (
    <TicketDrawerBody
      key={ticketKey}
      ticketKey={ticketKey}
      date={date}
      attendees={attendees}
      onClose={onClose}
    />
  );
}

function TicketDrawerBody({
  ticketKey,
  date,
  attendees,
  onClose,
}: TicketDrawerProps & { ticketKey: string }) {
  const [detail, setDetail] = useState<DeskTicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // THE SAFETY DEFAULT. This initialiser runs on every mount, and a mount is
  // exactly "the drawer opened". Nothing resets it because nothing has to.
  const [mode, setMode] = useState<ComposerMode>(INITIAL_COMPOSER_MODE);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [appended, setAppended] = useState<DeskThreadMessage[]>([]);
  const [discussed, setDiscussed] = useState(false);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // ── The reset. Every open of the drawer — new ticket or the same one
  // reopened — puts the composer back to INTERNAL and clears the draft. This
  // Load the detail. Note what this effect does NOT do: reset the mode. The
  // mount is the reset (see the header comment) — an effect that also had to
  // fire correctly would be a second, weaker guarantee.
  useEffect(() => {
    setLoading(true);
    const ctrl = new AbortController();
    getTicketDesk(ticketKey, date, ctrl.signal)
      .then((d) => {
        setDetail(d);
        setDiscussed(!!d.discussed);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [ticketKey, date]);

  // Escape closes, matching the scrim and the ✕.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [ticketKey, onClose]);

  const post = useCallback(async () => {
    if (!draft.trim() || posting) return;
    setPosting(true);
    setPostError(null);
    const res = await postDeskNote(ticketKey, { text: draft.trim(), mode });
    setPosting(false);
    if (!res.ok) {
      setPostError(res.error);
      return;
    }
    setAppended((prev) => [
      ...prev,
      {
        id: res.id || `local-${prev.length}`,
        name: "You",
        role: res.internal ? "internal" : "aeronet",
        at: res.created,
        text: res.text,
      },
    ]);
    setDraft("");
    // Back to the safe default after every post.
    setMode("internal");
  }, [ticketKey, draft, mode, posting]);

  const postStandupNote = useCallback(async () => {
    if (!draft.trim() || posting) return;
    setPosting(true);
    setPostError(null);
    const res = await postDeskNote(ticketKey, {
      text: draft.trim(),
      mode: "internal",
      standup: { date: date || new Date().toISOString().slice(0, 10), attendees },
    });
    setPosting(false);
    if (!res.ok) {
      setPostError(res.error);
      return;
    }
    setAppended((prev) => [
      ...prev,
      { id: res.id || `local-${prev.length}`, name: "You", role: "internal", at: res.created, text: res.text },
    ]);
    setDraft("");
    setMode("internal");
    if (!discussed) {
      setDiscussed(true);
      void postTicketDiscussed(ticketKey, true, date);
    }
  }, [ticketKey, draft, posting, date, attendees, discussed]);

  const toggleDiscussed = useCallback(async () => {
    const next = !discussed;
    setDiscussed(next);
    const res = await postTicketDiscussed(ticketKey, next, date);
    if (!res.ok) setDiscussed(!next);
  }, [ticketKey, discussed, date]);

  const look = composerLook(mode);
  const customerMode = look.loud;
  const thread = [...(detail?.thread ?? []), ...appended];
  const org = detail?.reporterOrg ?? detail?.customer ?? "The customer";

  return (
    <>
      <Box
        onClick={onClose}
        aria-hidden
        sx={{ position: "fixed", inset: 0, background: tokenScrim(), zIndex: 1290 }}
      />
      <Box
        role="dialog"
        aria-modal="true"
        aria-label={`Ticket ${ticketKey}`}
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 680,
          maxWidth: "100vw",
          background: c.pageBg,
          borderLeft: `1px solid ${c.border}`,
          boxShadow: brainTokens.shadow.widget,
          zIndex: 1291,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ---- Header ---- */}
        <Box sx={{ padding: "18px 24px 14px", borderBottom: `1px solid ${c.borderLight2}` }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box component="span" sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 500, color: c.tealInk }}>
              {ticketKey}
            </Box>
            {detail && (
              <Box
                component="span"
                sx={{
                  fontFamily: MONO,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.09em",
                  padding: "4px 9px",
                  borderRadius: `${brainTokens.radius.pill}px`,
                  whiteSpace: "nowrap",
                  background: detail.ballWithUs ? c.dangerBg : c.tealTint,
                  color: detail.ballWithUs ? c.dangerText : c.tealInk,
                }}
                title={`Idle since ${detail.lastActivityAt} (${detail.lastActivityKind})`}
              >
                {detail.ballWithUs ? "WITH US" : "MOVING"} · {detail.idleDays}D
              </Box>
            )}
            <Box sx={{ flex: 1 }} />
            <Box
              component="a"
              href={detail?.url ?? "#"}
              target="_blank"
              rel="noreferrer"
              sx={{ fontSize: 12.5, fontWeight: 500, whiteSpace: "nowrap", color: c.tealInk, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              Open in Jira ↗
            </Box>
            <Box
              component="button"
              ref={closeRef}
              onClick={onClose}
              aria-label="Close"
              sx={{
                width: 30,
                height: 30,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: `${brainTokens.radius.button}px`,
                border: `1px solid ${c.border}`,
                background: c.pageBg,
                color: c.muted,
                cursor: "pointer",
                fontSize: 15,
                lineHeight: 1,
                fontFamily: brainTokens.font.sans,
              }}
            >
              ✕
            </Box>
          </Box>
          <Box sx={{ fontSize: 21, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.35, mt: "10px", color: c.ink }}>
            {detail?.summary ?? (loading ? "Loading…" : ticketKey)}
          </Box>
          {detail && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", mt: "11px" }}>
              <PriorityPill priority={detail.scoreBucket} />
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "3px 11px", borderRadius: `${brainTokens.radius.pill}px`, background: c.borderLight2 }}
              >
                <Box aria-hidden sx={{ width: 7, height: 7, borderRadius: "50%", background: TONE_COLOR[DESK_STATE_TONE[detail.deskState]] }} />
                <Box component="span" sx={{ fontSize: 12.5, fontWeight: 500, color: c.ink3, whiteSpace: "nowrap" }}>
                  {detail.status}
                </Box>
              </Box>
              <Box component="span" sx={{ fontSize: 12.5, color: c.ink4, whiteSpace: "nowrap" }}>
                {detail.customer ?? "—"}
              </Box>
              <Box component="span" sx={{ fontSize: 12.5, color: c.muted2, whiteSpace: "nowrap" }}>
                {DESK_STATE_LABEL[detail.deskState]}
              </Box>
              {discussed && (
                <Box
                  component="span"
                  sx={{
                    fontFamily: MONO,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.09em",
                    padding: "4px 9px",
                    borderRadius: `${brainTokens.radius.pill}px`,
                    background: c.successBg,
                    color: c.successText,
                    whiteSpace: "nowrap",
                  }}
                >
                  DISCUSSED AT STANDUP
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* ---- Body ---- */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "18px 24px 22px" }}>
          {error && (
            <Box sx={{ fontSize: 13.5, color: c.dangerText }}>Could not load this ticket: {error}</Box>
          )}
          {!detail && !error && (
            <Box sx={{ fontSize: 13.5, color: c.muted }}>Loading the ticket from Jira…</Box>
          )}

          {detail && (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "12px 24px" }}>
                {[
                  { label: "Reporter", value: [detail.reporter, detail.reporterOrg].filter(Boolean).join(" · ") || "—" },
                  { label: "Assignee", value: detail.assigneeName ?? "Unassigned" },
                  {
                    label: "Epic · version",
                    value: [detail.epic, detail.fixVersions.join(", ") || detail.releaseLabels.join(", ")]
                      .filter(Boolean)
                      .join(" · ") || "—",
                  },
                  // "Raised" sits directly beside "Last activity" because the
                  // one thing the room must not misread on this tab is the Age
                  // axis: it measures time since the LAST ACTIVITY, never time
                  // since the ticket was raised. A 272-day-old ticket answered
                  // two days ago is ACTIVE, and printing both numbers side by
                  // side is what makes that legible rather than a rule people
                  // have to remember.
                  {
                    label: "Raised",
                    value: detail.created
                      ? `${daysSince(detail.created)}d ago · ${formatWhen(detail.created)}`
                      : "—",
                    mono: true,
                  },
                  {
                    label: "Last activity",
                    value: `${detail.idleDays}d ago · ${
                      detail.lastActivityKind === "awaiting"
                        ? "they replied"
                        : detail.lastActivityKind === "created"
                          ? "raised, never touched"
                          : detail.lastActivityKind
                    }`,
                    mono: true,
                    danger: detail.ballWithUs && detail.idleDays > 14,
                  },
                ].map((f) => (
                  <Box key={f.label} sx={{ display: "flex", alignItems: "baseline", gap: "10px", minWidth: 0 }}>
                    <Box sx={{ width: 112, flex: "none" }}>
                      <MonoLabelMicro>{f.label}</MonoLabelMicro>
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: f.mono ? 13 : 13.5,
                        fontFamily: f.mono ? MONO : brainTokens.font.sans,
                        fontWeight: f.mono ? 700 : 400,
                        color: f.danger ? c.dangerText : c.ink3,
                        lineHeight: 1.4,
                        // A reporter address like `techrecords@helicraftaviation.com`
                        // is one unbreakable word: without these it overflows its
                        // grid cell and prints straight over the ASSIGNEE label.
                        minWidth: 0,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {f.value}
                    </Box>
                  </Box>
                ))}
              </Box>

              {detail.description && (
                <Box sx={{ fontSize: 14, lineHeight: 1.65, color: c.ink3, mt: "18px" }}>{detail.description}</Box>
              )}

              <AgentPanel
                context="triage"
                stamp={
                  detail.triage
                    ? `${detail.triageStale ? "stale · " : ""}scored ${formatAgo(detail.triage.generatedAt)}`
                    : undefined
                }
                sx={{ mt: "14px" }}
              >
                {detail.triage ? (
                  <>
                    <Box>{detail.triage.recommendation}</Box>
                    <Box sx={{ mt: "8px", fontSize: 12.5, color: c.tealInk }}>
                      Suggestion · {detail.triage.disposition.replace(/_/g, " ")} · route {detail.triage.routing.lane}
                      {detail.triage.routing.module ? ` · ${detail.triage.routing.module}` : ""}
                      {detail.triage.suggestedAssignee ? ` · suggest ${detail.triage.suggestedAssignee}` : ""} ·{" "}
                      {Math.round(detail.triage.confidence * 100)}% confidence
                    </Box>
                  </>
                ) : (
                  "No triage verdict stored for this ticket yet. The nightly pass covers the most urgent open tickets; this one has not been through it."
                )}
              </AgentPanel>

              <Box sx={{ mt: "20px" }}>
                <MonoLabelMicro>Linked development</MonoLabelMicro>
                {detail.linked.length === 0 ? (
                  <Box sx={{ fontSize: 13, color: c.faint, mt: "8px" }}>
                    No development issue is linked to this ticket.
                  </Box>
                ) : (
                  detail.linked.map((l) => {
                    const spine = l.coversRelease
                      ? c.successDot
                      : l.statusCategory === "done"
                        ? c.teal
                        : l.fixVersions.length
                          ? c.warnDot
                          : c.dangerText;
                    return (
                      <Box
                        key={l.key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "11px 12px",
                          mt: "8px",
                          border: `1px solid ${c.border}`,
                          borderLeft: `3px solid ${spine}`,
                          borderRadius: `${brainTokens.radius.card}px`,
                        }}
                      >
                        <Box
                          component="a"
                          href={l.url}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ fontFamily: MONO, fontSize: 12, whiteSpace: "nowrap", color: c.tealInk, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                        >
                          {l.key}
                        </Box>
                        {/* This section lists EVERY linked issue, not only the
                            ones on the ticket's fix version — otherwise dev
                            work aimed at the wrong release renders as "no dev
                            work". The cost of that widening is that a
                            `relates to` or `is duplicated by` link (14 and 1
                            respectively on the live desk, one of them pointing
                            at another SUPPORT ticket) would sit under a heading
                            that asserts it is the development. So each row
                            states the link phrase Jira actually holds. */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box component="span" sx={{ fontSize: 13.5, color: c.ink3, lineHeight: 1.4 }}>
                            {l.summary}
                          </Box>
                          {l.linkType && (
                            <Box sx={{ mt: "3px" }}>
                              <MonoLabelMicro color={c.muted2}>{l.linkType}</MonoLabelMicro>
                            </Box>
                          )}
                        </Box>
                        <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, color: l.fixVersions.length ? c.ink4 : c.dangerText, whiteSpace: "nowrap" }}>
                          {l.fixVersions.length ? l.fixVersions.join(", ") : "no fix version"}
                        </Box>
                        <Box component="span" sx={{ fontSize: 12.5, color: c.ink4, whiteSpace: "nowrap" }}>
                          {l.status}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>

              <Box sx={{ mt: "22px" }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                  <MonoLabelMicro>Conversation</MonoLabelMicro>
                  <Box component="span" sx={{ fontSize: 12.5, color: c.muted2 }}>
                    {thread.length} {thread.length === 1 ? "message" : "messages"} · synced from Jira
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mt: "11px" }}>
                  {thread.length === 0 && (
                    <Box sx={{ fontSize: 13, color: c.faint }}>Nobody has commented on this ticket.</Box>
                  )}
                  {thread.map((m) => {
                    const role = ROLE_LOOK[m.role];
                    return (
                      <Box key={m.id} sx={{ padding: "0 0 14px 14px", borderLeft: `2px solid ${role.rule}` }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap" }}>
                          <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: "50%", flex: "none", background: role.dot }} />
                          <Box component="span" sx={{ fontSize: 13.5, fontWeight: 600, color: c.ink2 }}>
                            {m.name}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              fontFamily: MONO,
                              fontSize: 9.5,
                              fontWeight: 700,
                              letterSpacing: "0.09em",
                              padding: "3px 7px",
                              borderRadius: "4px",
                              background: role.bg,
                              color: role.fg,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {role.label}
                          </Box>
                          <Box sx={{ flex: 1 }} />
                          <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, color: c.muted2, whiteSpace: "nowrap" }}>
                            {formatWhen(m.at)}
                          </Box>
                        </Box>
                        <Box sx={{ fontSize: 14, lineHeight: 1.6, color: c.ink3, mt: "6px" }}>{m.text}</Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* ---- Composer ---- */}
        <Box
          data-testid="desk-composer"
          data-mode={mode}
          sx={{
            borderTop: `1px solid ${customerMode ? look.border : c.borderLight2}`,
            background: c.pageBg,
          }}
        >
          {customerMode && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "10px 24px",
                background: c.warnBg,
                borderBottom: `1px solid ${c.warnBorder}`,
                flexWrap: "wrap",
              }}
            >
              <Box component="span" aria-hidden sx={{ color: c.warnText, fontSize: 14 }}>
                ⚠
              </Box>
              <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: c.warnText, whiteSpace: "nowrap" }}>
                Replying to the customer
              </Box>
              <Box component="span" sx={{ fontSize: 12.5, color: c.warnText }}>
                {org} receives this by email, on the ticket
              </Box>
              <Box sx={{ flex: 1 }} />
              <Box
                component="button"
                onClick={() => setMode("internal")}
                sx={{
                  fontFamily: brainTokens.font.sans,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: c.warnText,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Back to internal note
              </Box>
            </Box>
          )}

          <Box sx={{ padding: "13px 24px 15px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <MonoLabelMicro color={customerMode ? look.accent : c.faint}>
                {look.label}
              </MonoLabelMicro>
              <Box sx={{ flex: 1 }} />
              {!customerMode && (
                <Box
                  component="button"
                  onClick={() => setMode("customer")}
                  sx={{
                    fontFamily: brainTokens.font.sans,
                    fontSize: 12.5,
                    fontWeight: 500,
                    color: c.muted,
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    "&:hover": { color: c.ink3 },
                  }}
                >
                  Reply to the customer instead →
                </Box>
              )}
            </Box>

            <Box
              component="textarea"
              value={draft}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
              placeholder={customerMode ? "Write the reply the room just agreed on…" : "What did we decide, and why…"}
              aria-label={customerMode ? "Customer reply" : "Internal note"}
              sx={{
                width: "100%",
                boxSizing: "border-box",
                mt: "9px",
                minHeight: 72,
                resize: "vertical",
                fontFamily: brainTokens.font.sans,
                fontSize: 14,
                lineHeight: 1.6,
                color: c.ink,
                padding: "11px 13px",
                borderRadius: `${brainTokens.radius.card}px`,
                background: c.pageBg,
                border: `1px solid ${look.border}`,
                outline: "none",
              }}
            />

            {postError && (
              <Box sx={{ fontSize: 12.5, color: c.dangerText, mt: "8px" }}>{postError}</Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: "9px", mt: "10px", flexWrap: "wrap" }}>
              {CANNED[mode].map((k) => (
                <Box
                  key={k.label}
                  component="button"
                  onClick={() => setDraft(k.text)}
                  sx={{
                    fontFamily: brainTokens.font.sans,
                    fontSize: 12.5,
                    fontWeight: 500,
                    color: c.muted,
                    background: "transparent",
                    border: `1px solid ${c.border}`,
                    borderRadius: `${brainTokens.radius.pill}px`,
                    padding: "5px 11px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {k.label}
                </Box>
              ))}
              <Box sx={{ flex: 1 }} />
              <Box
                component="button"
                onClick={toggleDiscussed}
                sx={{
                  fontFamily: brainTokens.font.sans,
                  fontSize: 13,
                  fontWeight: 500,
                  padding: "8px 14px",
                  borderRadius: `${brainTokens.radius.button}px`,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  background: discussed ? c.successBg : c.pageBg,
                  color: discussed ? c.successText : c.ink3,
                  border: `1px solid ${discussed ? c.successBorder : c.dropBorder}`,
                }}
              >
                Mark discussed
              </Box>
              {!customerMode && (
                <Box
                  component="button"
                  onClick={postStandupNote}
                  disabled={!draft.trim() || posting}
                  title="Writes an internal Jira comment stamped with today's date and who was in the room"
                  sx={{
                    fontFamily: brainTokens.font.sans,
                    fontSize: 13,
                    fontWeight: 500,
                    padding: "8px 14px",
                    borderRadius: `${brainTokens.radius.button}px`,
                    cursor: draft.trim() && !posting ? "pointer" : "not-allowed",
                    opacity: draft.trim() && !posting ? 1 : 0.5,
                    whiteSpace: "nowrap",
                    background: c.pageBg,
                    color: c.tealInk,
                    border: `1px solid ${c.tealTintBorder}`,
                  }}
                >
                  Post standup note
                </Box>
              )}
              <Box
                component="button"
                onClick={post}
                disabled={!draft.trim() || posting}
                sx={{
                  fontFamily: brainTokens.font.sans,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "8px 16px",
                  borderRadius: `${brainTokens.radius.button}px`,
                  cursor: draft.trim() && !posting ? "pointer" : "not-allowed",
                  whiteSpace: "nowrap",
                  opacity: draft.trim() && !posting ? 1 : 0.5,
                  background: look.accent,
                  color: c.white,
                  border: `1px solid ${look.accent}`,
                }}
              >
                {posting ? "Posting…" : look.submitLabel}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

/** The drawer scrim — the handoff's `rgba(16,24,40,.32)`, built from the ink token. */
function tokenScrim(): string {
  const h = c.ink.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, 0.32)`;
}
